import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Input } from '../../design-system/components/Input';
import { langLabelSlotStyles } from '../../design-system/patterns/FormLayout';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
`;

const FieldLabel = styled.span<{ error?: boolean }>`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => (props.error ? props.theme.colors.error : props.theme.colors.text)};
  font-family: ${typography.fontFamily.primary};
`;

const DoubleInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[4]};
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const LangBadge = styled.span<{ $variant: 'en' | 'gr' }>`
  ${langLabelSlotStyles}
  background: ${props =>
    props.$variant === 'gr'
      ? `${props.theme.colors.primary}18`
      : `${props.theme.colors.textSecondary}18`};
  color: ${props =>
    props.$variant === 'gr' ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border-color: ${props =>
    props.$variant === 'gr'
      ? `${props.theme.colors.primary}40`
      : `${props.theme.colors.border}`};
`;
const ErrorText = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

export interface BilingualDoubleInputProps {
  label: string;
  valueEn: string;
  valueGr: string;
  onChangeEn: (value: string) => void;
  onChangeGr: (value: string) => void;
  type?: 'text' | 'tel' | 'email';
  error?: string;
  placeholderEn?: string;
  placeholderGr?: string;
  required?: boolean;
  /** When true, use short language labels (Ελ/Αγ) instead of full (Ελληνικά/Αγγλικά). */
  shortLabels?: boolean;
  /** When true, show Greek field first (better for Greek UI). */
  greekFirst?: boolean;
  /** Optional stable id prefix for both language fields. */
  idPrefix?: string;
}

export const BilingualDoubleInput: React.FC<BilingualDoubleInputProps> = ({
  label,
  valueEn,
  valueGr,
  onChangeEn,
  onChangeGr,
  type = 'text',
  error,
  placeholderEn,
  placeholderGr,
  required = false,
  shortLabels = false,
  greekFirst = false,
  idPrefix,
}) => {
  const { t, i18n } = useTranslation();
  const reactId = useId();
  const baseId = idPrefix || `bilingual-${reactId}`;
  const enId = `${baseId}-en`;
  const grId = `${baseId}-gr`;
  const errorId = error ? `${baseId}-error` : undefined;
  const useGreekFirst = greekFirst || (i18n.language || 'en') === 'gr';
  const labelEn = shortLabels ? t('register.languageShortEn') : t('register.languageEn');
  const labelGr = shortLabels ? t('register.languageShortGr') : t('register.languageGr');
  const accessibleNameEn = `${label} — ${labelEn}`;
  const accessibleNameGr = `${label} — ${labelGr}`;

  const enField = (
    <InputGroup key="en">
      <LangBadge $variant="en" id={`${enId}-badge`}>
        {labelEn}
      </LangBadge>
      <Input
        id={enId}
        type={type}
        value={valueEn}
        onChange={e => onChangeEn(e.target.value)}
        placeholder={placeholderEn}
        fullWidth
        variant="outlined"
        error={!!error}
        aria-label={accessibleNameEn}
        aria-invalid={!!error || undefined}
        aria-describedby={errorId}
        aria-required={required || undefined}
      />
    </InputGroup>
  );

  const grField = (
    <InputGroup key="gr">
      <LangBadge $variant="gr" id={`${grId}-badge`}>
        {labelGr}
      </LangBadge>
      <Input
        id={grId}
        type={type}
        value={valueGr}
        onChange={e => onChangeGr(e.target.value)}
        placeholder={placeholderGr}
        fullWidth
        variant="outlined"
        error={!!error}
        aria-label={accessibleNameGr}
        aria-invalid={!!error || undefined}
        aria-describedby={errorId}
        aria-required={required || undefined}
      />
    </InputGroup>
  );

  return (
    <Wrapper>
      <FieldLabel error={!!error} id={`${baseId}-group-label`}>
        {label}
        {required && <span style={{ color: 'var(--color-error, #dc3545)', marginLeft: 2 }}>*</span>}
      </FieldLabel>
      <DoubleInputRow role="group" aria-labelledby={`${baseId}-group-label`}>
        {useGreekFirst ? [grField, enField] : [enField, grField]}
      </DoubleInputRow>
      {error && (
        <ErrorText id={errorId} role="alert">
          {error}
        </ErrorText>
      )}
    </Wrapper>
  );
};

export default BilingualDoubleInput;
